import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { SparkDialog } from 'sharedComponents/UI';
import MemoryModal from './MemoryModal';
import PluginModal from './PluginModal';
import { useSparkStepTypes } from 'hooks/EntityTypes/useSparkStepTypes';
import { BackIcon, PlusIcon } from 'components/SVG';
import { STEP_TYPE_NAMES } from 'constants/SparkSteps';
import { useAddSparkStep } from 'hooks/SparkBuilder/sparkStep';
import { useSparkBuilderStore } from 'stores';
import { useReactFlowStore } from 'components/CreateSpark/SparkBuilderCanvas/hooks';
import SparkSearchContextProvider from 'components/Providers/ContextProviders/SparkSearchContextProviderV2';

type SelectStepTypeModalProps = {
  open: boolean;
};
type StepTypeContent = 'default' | 'plugin' | 'knowledge';

function SelectStepTypeModal({ open }: SelectStepTypeModalProps) {
  const sparkID = useSparkBuilderStore((store) => store.sparkID);
  const { addSelectedNodes } = useReactFlowStore();

  const setSelectedStep = useSparkBuilderStore(
    (store) => store.builderSideBarActions.setSelectedStep
  );
  const onClose = useSparkBuilderStore(
    (store) => store.canvasActions.closeAddDialog
  );
  const setStepOrder = useSparkBuilderStore(
    (store) => store.canvasActions.setStepOrder
  );

  const stepOrder = useSparkBuilderStore((store) => store.canvas.stepOrder);
  const { sparkStepTypes, isLoading } = useSparkStepTypes();
  const [selectedStepType, setSelectedStepType] =
    React.useState<StepTypeContent>('default');
  const [selectedStepTypeID, setSelectedStepTypeID] =
    React.useState<string>('');

  const { mutateAsync: addSparkStep } = useAddSparkStep();

  if (isLoading) {
    return <CircularProgress />;
  }

  const handleSelectStepType = async (
    stepTypeID: string,
    resourceID?: string
  ) => {
    setSelectedStep(null);
    if (!stepTypeID || !stepOrder || !sparkID) {
      return;
    }
    const id = uuidv4();
    setStepOrder(null);
    onClose();

    await addSparkStep({
      id,
      stepTypeID,
      stepOrder,
      sparkID,
      ...(selectedStepType === 'knowledge' && { memoryID: resourceID }),
      ...(selectedStepType === 'plugin' && { pluginID: resourceID }),
    });
    setSelectedStep({ type: 'sparkStep', id });
    addSelectedNodes([id]);
  };

  const handleTypeClick = (typeID: string, functionKey: StepTypeContent) => {
    if (functionKey === STEP_TYPE_NAMES.PROMPT) {
      handleSelectStepType(typeID);
    } else {
      setSelectedStepType(functionKey);
      setSelectedStepTypeID(typeID);
    }
  };

  const selectStepTypeModalContent = {
    createTitle: function ({
      icon,
      title,
    }: {
      icon: React.ReactNode;
      title: string;
    }) {
      return (
        <>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            gap={2}
          >
            <Box>{icon}</Box>
            <Typography>{title}</Typography>
          </Box>
        </>
      );
    },
    default: {
      title: 'Add a Step',
      icon: <PlusIcon color='white' />,
      content: (
        <>
          <Grid container justifyContent='center' gap={2} wrap='nowrap'>
            {sparkStepTypes?.map((type) => (
              <Grid key={type.id} item xs={4}>
                <ButtonBase
                  onClick={() =>
                    handleTypeClick(
                      type.id,
                      type.attributes.key as StepTypeContent
                    )
                  }
                  sx={{ width: '100%' }}
                >
                  <Card
                    elevation={0}
                    style={{ maxWidth: '100%' }}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: (theme) =>
                        `1px solid ${theme.palette.borderColor}`,
                      '&:hover': {
                        borderColor: (theme) => theme.palette.primary.main,
                      },
                      '&:active': { transform: 'scale(0.98)' },
                      '.MuiCardHeader-avatar': {
                        alignSelf: 'flex-start',
                      },
                    }}
                  >
                    <CardMedia
                      component='img'
                      src={type.attributes.imageURL}
                      alt={type.attributes.name}
                      title={type.attributes.name}
                      sx={{
                        aspectRatio: 1,
                        width: '94px',
                        alignSelf: 'center',
                        marginTop: 1,
                      }}
                    />
                    <CardContent sx={{ height: '100px' }}>
                      <Typography gutterBottom variant='h5' component='div'>
                        {type.attributes.name}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {type.attributes.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        </>
      ),
    },
    plugin: {
      title: 'Plugins',
      icon: <BackButton />,
      content: (
        <PluginModal
          onClick={handleSelectStepType}
          typeID={selectedStepTypeID}
        />
      ),
    },
    knowledge: {
      title: 'Knowledge Sources',
      icon: <BackButton />,
      content: (
        <SparkSearchContextProvider>
          <MemoryModal
            onClick={handleSelectStepType}
            typeID={selectedStepTypeID}
          />
        </SparkSearchContextProvider>
      ),
    },
  };
  function BackButton() {
    return (
      <Button
        onClick={() => setSelectedStepType('default')}
        sx={{
          minWidth: 'unset',
          width: '40px',
          height: '40px',
          padding: 0,
          borderWidth: '1px',
          borderRadius: '14px',
          aspectRatio: 1,
          backgroundColor: '#fff',
          color: 'var( --color-primary)',
          '&:hover': {
            backgroundColor: '#fff',
            color: 'var( --color-primary)',
          },
        }}
        variant='contained'
      >
        <BackIcon stroke='currentColor' />
      </Button>
    );
  }
  return (
    <SparkDialog
      title={selectStepTypeModalContent.createTitle({
        icon: selectStepTypeModalContent[selectedStepType].icon,
        title: selectStepTypeModalContent[selectedStepType].title,
      })}
      open={open}
      onClose={onClose}
      dialogContentStyles={{ position: 'relative' }}
    >
      {selectStepTypeModalContent[selectedStepType].content}
    </SparkDialog>
  );
}

export default SelectStepTypeModal;
